// -----------------------------------------------------------
// FloraIQ - Ficha botanica da especie (dicionario do CTA)
// -----------------------------------------------------------
// Enquanto a ficha nao estiver REVISADA, o selo de rascunho fica visivel no topo:
// orientacao de manejo nao conferida por tecnico nao pode parecer oficial.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { hasPermission } from "@/lib/auth";
import { SPECIES_ORIGIN_LABELS, codeLabel } from "@/config/projetos-config";
import type {
  ReviewStatus,
  SpeciesPestFields,
  SpeciesProfileCatalogs,
  SpeciesProfileFields,
  SpeciesProfileHistoryEntry,
  SpeciesProfileResponse,
} from "@/types/species-profile";
import ProfileView from "./components/ProfileView";
import ProfileForm from "./components/ProfileForm";
import PestsSection from "./components/PestsSection";
import ProfileHistory from "./components/ProfileHistory";
import { REVIEW_ICON, REVIEW_TONE, fmtDate } from "./profileFormat";

const REVIEW_TEXT: Record<ReviewStatus, { title: string; body: string }> = {
  RASCUNHO_IA: {
    title: "Rascunho gerado por IA",
    body: "Conteúdo redigido por IA a partir de literatura geral, com valores aproximados. Precisa ser conferido por um técnico antes de servir como orientação oficial.",
  },
  EM_REVISAO: {
    title: "Em revisão",
    body: "A ficha foi alterada e aguarda a revisão de um técnico responsável.",
  },
  REVISADO: { title: "Ficha revisada", body: "" },
};

export default function SpeciesProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const speciesId = Number(id);

  const [data, setData] = useState<SpeciesProfileResponse | null>(null);
  const [catalogs, setCatalogs] = useState<SpeciesProfileCatalogs | null>(null);
  const [history, setHistory] = useState<SpeciesProfileHistoryEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState("");

  const canEdit = hasPermission("tree.update");
  const canReview = hasPermission("inspection.homologate");

  const loadHistory = useCallback(() => {
    api.speciesProfileHistory(speciesId).then(setHistory).catch(() => setHistory([]));
  }, [speciesId]);

  useEffect(() => {
    if (!Number.isInteger(speciesId) || speciesId <= 0) {
      setLoadError("Espécie inválida.");
      return;
    }
    let alive = true;
    Promise.all([api.speciesProfile(speciesId), api.speciesProfileCatalogs()])
      .then(([d, c]) => {
        if (!alive) return;
        setData(d);
        setCatalogs(c);
      })
      .catch((e: Error) => alive && setLoadError(e.message));
    loadHistory();
    return () => {
      alive = false;
    };
  }, [speciesId, loadHistory]);

  async function run(action: () => Promise<SpeciesProfileResponse>, okMessage: string): Promise<boolean> {
    setBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      setData(await action());
      setNotice(okMessage);
      loadHistory();
      return true;
    } catch (e) {
      setActionError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function save(changes: Partial<SpeciesProfileFields>, note: string) {
    const ok = await run(
      () => api.updateSpeciesProfile(speciesId, { ...changes, ...(note ? { change_note: note } : {}) }),
      "Alterações salvas. A ficha está em revisão."
    );
    if (ok) {
      setEditing(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  async function review() {
    const ok = await run(() => api.reviewSpeciesProfile(speciesId, reviewNote.trim()), "Ficha marcada como revisada.");
    if (ok) {
      setReviewOpen(false);
      setReviewNote("");
    }
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center">
        <i className="ri-error-warning-line text-3xl text-danger-600" />
        <p className="mt-2 text-foreground-700">{loadError}</p>
        <Link to="/configuracoes" className="btn-outline mt-4">Voltar ao catálogo</Link>
      </div>
    );
  }
  if (!data || !catalogs) {
    return <div className="py-16 text-center text-foreground-500">Carregando ficha...</div>;
  }

  const { species, profile, pests } = data;
  const status = profile?.review_status;

  return (
    <div className="max-w-6xl mx-auto space-y-4 pb-10">
      <button type="button" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/configuracoes"))}
        className="text-sm text-foreground-500 hover:text-primary-700 inline-flex items-center gap-1">
        <i className="ri-arrow-left-line" /> Voltar
      </button>

      <header className="bg-white rounded-xl border border-background-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-foreground-500">Ficha botânica</p>
            <h1 className="text-xl font-semibold text-foreground-900">{species.popular_name}</h1>
            <p className="text-foreground-600 italic">{species.scientific_name}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {species.family_name && <span className="badge bg-background-100 text-foreground-700">{species.family_name}</span>}
              {species.origin && <span className="badge bg-background-100 text-foreground-700">{codeLabel(SPECIES_ORIGIN_LABELS, species.origin)}</span>}
              {species.protected_species && <span className="badge bg-primary-50 text-primary-700">Protegida</span>}
              {species.invasive_species && <span className="badge bg-danger-50 text-danger-700">Invasora</span>}
              <span className="badge bg-background-100 text-foreground-700">
                {species.registered_tree_count} exemplar(es) no inventário
              </span>
            </div>
          </div>
          {!editing && (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <button type="button" className="btn-outline" onClick={() => { setEditing(true); setNotice(null); setActionError(null); }}>
                  <i className="ri-edit-line" /> {profile ? "Editar ficha" : "Criar ficha"}
                </button>
              )}
              {canReview && profile && status !== "REVISADO" && (
                <button type="button" className="btn-primary" onClick={() => setReviewOpen(true)}>
                  <i className="ri-shield-check-line" /> Marcar como revisada
                </button>
              )}
            </div>
          )}
        </div>

        {status && (
          <div className={`mt-4 rounded-lg border px-4 py-3 text-sm flex gap-3 ${REVIEW_TONE[status]}`}>
            <i className={`${REVIEW_ICON[status]} text-lg leading-5`} />
            <div>
              <p className="font-semibold">{REVIEW_TEXT[status].title}</p>
              {status === "REVISADO" ? (
                <p>Revisada por {profile.reviewed_by_name ?? "técnico"} em {fmtDate(profile.reviewed_at)} · versão {profile.version}</p>
              ) : (
                <p>{REVIEW_TEXT[status].body}</p>
              )}
              {status === "EM_REVISAO" && profile.reviewed_at && (
                <p className="text-xs mt-1 opacity-80">
                  Última revisão: {profile.reviewed_by_name ?? "técnico"}, em {fmtDate(profile.reviewed_at)}.
                </p>
              )}
            </div>
          </div>
        )}

        {reviewOpen && (
          <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50/40 p-4 space-y-3">
            <p className="text-sm text-foreground-800">
              Ao marcar como revisada, você confirma que conferiu o conteúdo desta ficha e das pragas e doenças listadas.
              Seu nome e a data ficam registrados.
            </p>
            <label className="block text-xs font-medium text-foreground-600">
              Observação da revisão (opcional)
              <input type="text" maxLength={500} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-200" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-outline" disabled={busy} onClick={() => setReviewOpen(false)}>Cancelar</button>
              <button type="button" className="btn-primary disabled:opacity-50" disabled={busy} onClick={review}>
                {busy ? "Registrando..." : "Confirmar revisão"}
              </button>
            </div>
          </div>
        )}

        {notice && <p className="mt-3 text-sm text-primary-700" role="status"><i className="ri-check-line mr-1" />{notice}</p>}
        {actionError && !editing && (
          <p className="mt-3 text-sm text-danger-700" role="alert"><i className="ri-error-warning-line mr-1" />{actionError}</p>
        )}
      </header>

      {editing ? (
        <ProfileForm
          initial={profile}
          catalogs={catalogs}
          saving={busy}
          error={actionError}
          onCancel={() => { setEditing(false); setActionError(null); }}
          onSave={save}
        />
      ) : profile ? (
        <ProfileView profile={profile} catalogs={catalogs} />
      ) : (
        <div className="bg-white rounded-xl border border-dashed border-background-300 p-10 text-center">
          <i className="ri-book-open-line text-3xl text-foreground-400" />
          <p className="mt-2 text-foreground-700">Esta espécie ainda não tem ficha botânica.</p>
          {canEdit && (
            <button type="button" className="btn-primary mt-4" onClick={() => setEditing(true)}>Criar ficha</button>
          )}
        </div>
      )}

      {!editing && (
        <>
          <PestsSection
            pests={pests}
            catalogs={catalogs}
            canEdit={canEdit}
            busy={busy}
            onAdd={(v: SpeciesPestFields) => run(() => api.addSpeciesPest(speciesId, v), "Praga/doença adicionada. A ficha está em revisão.")}
            onUpdate={(pid, v) => run(() => api.updateSpeciesPest(speciesId, pid, v), "Praga/doença atualizada. A ficha está em revisão.")}
            onDelete={(pid) => run(() => api.deleteSpeciesPest(speciesId, pid), "Praga/doença removida. A ficha está em revisão.")}
          />
          <ProfileHistory entries={history} />
        </>
      )}
    </div>
  );
}
